import React from 'react';
import {
	Box,
	VStack,
	HStack,
	Button,
	chakra,
	Checkbox,
	CloseButton,
	Separator,
	Flex,
	Heading,
	RadioGroup,
	Input,
	NativeSelect,
	Switch,
	Table,
	Text,
	Portal,
	Dialog,
	Icon
} from '@chakra-ui/react';
import groupBy from 'lodash/groupBy';
import mapValues from 'lodash/mapValues';
import DatePicker from 'react-datepicker';
import { FaSortDown, FaSortUp } from 'react-icons/fa6';
import {
	useReactTable,
	getCoreRowModel,
	getSortedRowModel,
	flexRender,
	createColumnHelper
} from '@tanstack/react-table';
import Autocomplete from '../components/Autocomplete';
import SafetyObservation from './SafetyObservation';
import SafetyInspection from './SafetyInspection';
import theme from '../theme';
import { getSafetyFormSubmission, searchSafetyDatabase } from '../lib/safetyForms';
import MultiRef from 'react-multi-ref';
import moment from 'moment';

import 'react-datepicker/dist/react-datepicker.css';

const forms = {
	safety_observation: 'Safety Observation',
	safety_inspection: 'Safety Inspection'
};

const filterCategories = {
	activity: 'Activity',
	type_of_observation: 'Type of Observation',
	conditions_unsafe_acts: 'Conditions / Unsafe Acts',
	hazard_status: 'Hazard Status'
};

const selectOptions = {
	activity: {
		diving: 'Diving',
		topside: 'Topside',
		travel: 'Travel',
		shop: 'Shop',
		other: 'Other'
	},
	type_of_observation: {
		self_observation: 'Self-Observation',
		other_person: 'Other Person',
		short_service: 'Short Service',
		suggestion: 'Suggestion',
		near_miss: 'Near Miss'
	},
	conditions_unsafe_acts: {
		select: {
			safe: 'Safe',
			concern: 'Concern'
		},
		eye_face_protection: 'Eye / Face Protection',
		head_protection: 'Head Protection',
		hand_protection: 'Hand Protection',
		footwear: 'Footwear',
		hearing_protection: 'Hearing Protection',
		pfd_use_condition: 'PFD Use / Condition',
		fall_protection: 'Fall Protection',
		respirator: 'Respirator',
		line_of_fire: 'Line of Fire',
		walking: 'Walking',
		climbing: 'Climbing',
		eyes_on_work: 'Eyes on Work',
		lifting_carrying: 'Lifting / Carrying',
		slip_trip_hazards: 'Slip / Trip Hazards',
		pinch_points: 'Pinch Points',
		proper_pace: 'Proper Pace',
		selection_use: 'Selection & Use of Tools',
		condition: 'Condition of Tools',
		guards: 'Tool Guards',
		cords_lines: 'Tool Cords / Lines',
		vehicle_boat: 'Vehicle / Boat',
		rigging: 'Rigging',
		fire_first_aid: 'Fire / First Aid',
		sds_reviewed: 'SDS Reviewed',
		hazards_id_d: "Hazards ID'd",
		lockout_tagout: 'Lockout / Tagout',
		lighting_visibility: 'Lighting / Visibility',
		signs_barricades: 'Signs / Barricades',
		housekeeping: 'Housekeeping',
		weather: 'Weather',
		adjacent_work: 'Adjacent Work',
		communication: 'Communication',
		improper_tool_use: 'Improper Tool Use',
		climbing_structure: 'Climbing Structure',
		reckless_driving: 'Reckless Driving',
		improper_tie_off: 'Improper Tie-Off',
		permit_violation: 'Permit Violation',
		client_rule_violation: 'Client Rule Violation',
		walking_under_load: 'Walking Under Load',
		bypassing_safety_device: 'Bypassing Safety Device',
		working_impaired: 'Working Impaired',
		crossing_barricade: 'Crossing Barricade',
		endangering_others: 'Endangering Others',
		exceeds_skills_training: 'Exceeds Skills / Training'
	},
	hazard_status: {
		completed: 'Completed',
		pending: 'Pending'
	}
};

const handlePDFExport = exportList =>
	window.open(window.location.origin + '/generatesafetypdf?sids=' + exportList.join(','), '_blank');

const handleExcelExport = exportList =>
	window.open(
		window.location.origin +
			'/dashboard/safety-database/results/download?exporter=safety_xlsx&multiple_delimiter=;&header_format=label&header_prefix=1&header_prefix_label_delimiter=:%20&header_prefix_key_delimiter=__&options_single_format=compact&options_multiple_format=compact&options_item_format=label&composite_element_item_format=label&entity_reference_items=title&excluded_columns=serial,uid,uuid,token,uri,created,completed,changed,in_draft,current_page,remote_addr,langcode,webform_id,entity_type,entity_id,locked,sticky,notes,form_tags&download=1&files=1&order=asc&sticky=0&state=all&archive_type=zip&file_name=[webform_submission:sid]&' +
			Object.entries(exportList)
				.map(([id, sids]) => `sids[${id}]=${sids.join(',')}`)
				.join('&'),
		'_blank'
	);

const IndeterminateCheckbox = ({ indeterminate, ...rest }) => (
	<Checkbox.Root checked={indeterminate ? 'indeterminate' : rest.checked} {...rest}>
		<Checkbox.HiddenInput />
		<Checkbox.Control />
	</Checkbox.Root>
);

export default () => {
	const [hasSearched, setHasSearched] = React.useState(false);
	const [form, setForm] = React.useState('');
	const [data, setData] = React.useState([]);
	const [searchTime, setSearchTime] = React.useState(moment().format('hh:mm:ss a'));
	const [startDate, setStartDate] = React.useState(new Date(new Date().getFullYear(), new Date().getMonth(), 1));
	const [endDate, setEndDate] = React.useState(new Date(new Date().getFullYear(), new Date().getMonth() + 1, 0));
	const [calendar, setCalendar] = React.useState({
		start: false,
		end: false
	});
	const [location, setLocation] = React.useState('');
	const [returnForm, setReturnForm] = React.useState('');
	const [observer, setObserver] = React.useState('');
	const [supervisor, setSupervisor] = React.useState('');
	const [hasPictures, setHasPictures] = React.useState(false);
	const [operator, setOperator] = React.useState('and');
	const [filters, setFilters] = React.useState([{ category: '', type: '', select: '' }]);
	const [removeFilterDialogOpen, setRemoveFilterDialogOpen] = React.useState(null);
	const [submission, setSubmission] = React.useState(null);
	const [errorDialog, setErrorDialog] = React.useState(null);
	const [rowSelection, setRowSelection] = React.useState({});
	const [sorting, setSorting] = React.useState([]);

	const errorModalRef = React.useRef();
	const [filterRefs] = React.useState(() => new MultiRef());

	React.useEffect(() => {
		if (window.id && window.sid) {
			getSafetyFormSubmission(window.id, window.sid)
				.then(data =>
					setSubmission({
						...data,
						sid: window.sid
					})
				)
				.catch(error =>
					setErrorDialog({
						header: 'Error Fetching Submission',
						body: error.message
					})
				);
		}
	}, [window.id, window.sid]);

	const columnHelper = createColumnHelper();

	const columns = React.useMemo(
		() =>
			form === 'safety_observation'
				? [
						columnHelper.accessor('date', {
							header: 'Date',
							minSize: 150
						}),
						columnHelper.accessor('observer_name_completed_by', {
							header: 'Completed By'
						}),
						columnHelper.accessor('location', {
							header: 'Location'
						}),
						columnHelper.accessor('activity', {
							header: 'Activity',
							cell: cell =>
								cell.getValue().length ? (
									<ul>
										{cell.getValue().map(activity => (
											<li key={activity}>{activity}</li>
										))}
									</ul>
								) : null
						}),
						columnHelper.accessor('type_of_observation', {
							header: 'Type of Observation'
						}),
						columnHelper.accessor('what', {
							header: 'What: Description'
						}),
						columnHelper.accessor('has_pictures', {
							header: 'Has Pictures',
							cell: cell => (cell.getValue() ? 'Yes' : 'No')
						})
					]
				: form === 'safety_inspection'
					? [
							columnHelper.accessor('date', {
								header: 'Date',
								minSize: 150
							}),
							columnHelper.accessor('observer_name_completed_by', {
								header: 'Completed By'
							}),
							columnHelper.accessor('project_number', {
								header: 'Project Number'
							}),
							columnHelper.accessor('location', {
								header: 'Location'
							}),
							columnHelper.accessor('description', {
								header: 'Description'
							})
						]
					: [
							columnHelper.accessor('date', {
								header: 'Date',
								minSize: 150
							}),
							columnHelper.accessor('observer_name_completed_by', {
								header: 'Completed By'
							}),
							columnHelper.accessor('location', {
								header: 'Location'
							}),
							columnHelper.accessor('id', {
								header: 'Type of Form',
								cell: cell => forms[cell.getValue()]
							}),
							columnHelper.accessor('supervisor', {
								header: 'Supervisor'
							}),
							columnHelper.accessor('description', {
								header: 'Description'
							}),
							columnHelper.accessor('has_pictures', {
								header: 'Has Pictures',
								cell: cell => (cell.getValue() ? 'Yes' : 'No')
							})
						],
		[form]
	);

	const locationList = React.useMemo(
		() =>
			window.projectList.map(project => ({
				project: `${project.phase_name} - ${project.project_name}: ${project.project_number}-${project.phase_number}`
			})),
		[window.projectList]
	);

	const employeeList = React.useMemo(
		() => window.employeeList.map(({ name }) => ({ employee: name })),
		[window.employeeList]
	);

	const table = useReactTable({
		columns: [
			{
				id: 'selection',
				// The header can use the table's
				// getToggleAllRowsSelectedProps method to render a
				// checkbox
				header: ({ table }) => (
					<IndeterminateCheckbox
						{...{
							checked: table.getIsAllRowsSelected(),
							indeterminate: table.getIsSomeRowsSelected(),
							onChange: table.getToggleAllRowsSelectedHandler()
						}}
					/>
				),
				// The cell can use the individual row's
				// getToggleRowSelectedProps method to the render a
				// checkbox
				cell: ({ row }) => (
					<IndeterminateCheckbox
						{...{
							checked: row.getIsSomeSelected() ? 'indeterminate' : row.getIsSelected(),
							disabled: !row.getCanSelect(),
							onChange: row.getToggleSelectedHandler()
						}}
					/>
				)
			},
			...columns
		],
		data,
		state: {
			rowSelection,
			sorting
		},
		enableRowSelection: true,
		enableSorting: true,
		onRowSelectionChange: setRowSelection,
		onSortingChange: setSorting,
		getCoreRowModel: getCoreRowModel(),
		getSortedRowModel: getSortedRowModel(),
		initialState: {
			sorting: [
				{
					id: 'date',
					desc: true
				}
			]
		}
	});

	const clearSearch = (clearFilters = true) => {
		setHasSearched(false);
		setData([]);

		if (clearFilters) {
			setForm('');
			setStartDate(new Date(new Date().getFullYear(), new Date().getMonth(), 1));
			setEndDate(new Date(new Date().getFullYear(), new Date().getMonth() + 1, 0));
			setLocation('');
			setReturnForm('');
			setObserver('');
			setHasPictures(false);
			setOperator('and');
			setFilters([{ category: '', type: '', select: '' }]);
		}
	};

	const search = _form => {
		if (!hasSearched) {
			setHasSearched(true);
		}

		searchSafetyDatabase({
			startDate: startDate ? startDate.getTime() / 1000 : null,
			endDate: endDate ? endDate.getTime() / 1000 : null,
			location,
			returnForm: _form || form || returnForm,
			observer,
			supervisor,
			hasPictures,
			operator,
			filters
		})
			.then(({ data: forms }) => {
				setData(forms);
				setSearchTime(moment().format('hh:mm:ss a'));
			})
			.catch(error => {
				setErrorDialog({
					header: 'Error Searching Database',
					body: error.message
				});
			});
	};

	return submission ? (
		submission.id === 'safety_observation' ? (
			<SafetyObservation submission={submission} handleExport={handlePDFExport} />
		) : (
			<SafetyInspection submission={submission} id={submission.sid} handleExport={handlePDFExport} />
		)
	) : (
		<Box px={4} pt={4} pb={100}>
			<Dialog.Root
				role="alertdialog"
				initialFocusEl={errorModalRef}
				onOpenChange={({ open }) => {
					if (!open) {
						setErrorDialog(null);
					}
				}}
				open={Boolean(errorDialog)}
				placement="center"
			>
				<Portal>
					<Dialog.Backdrop />
					<Dialog.Positioner>
						<Dialog.Content opacity="1 !important">
							<Dialog.Header>
								<Dialog.Title fontSize="lg" fontWeight="bold">
									{errorDialog?.header}
								</Dialog.Title>
							</Dialog.Header>
							<Dialog.Body>
								<p>{errorDialog?.body}</p>
							</Dialog.Body>
							<Dialog.Footer>
								<Dialog.ActionTrigger asChild>
									<Button
										ref={errorModalRef}
										ml={3}
										color={theme.colors.white}
										bg={theme.colors.blue[700]}
										_hover={{ backgroundColor: theme.colors.blue[800] }}
										onClick={() => setErrorDialog(null)}
									>
										Okay
									</Button>
								</Dialog.ActionTrigger>
							</Dialog.Footer>
							<Dialog.CloseTrigger asChild>
								<CloseButton size="sm" />
							</Dialog.CloseTrigger>
						</Dialog.Content>
					</Dialog.Positioner>
				</Portal>
			</Dialog.Root>
			<Heading mx={4} my={6} size="lg" color={theme.colors.blue[700]}>
				Safety Database
			</Heading>
			<Flex>
				{Object.keys(forms).map(_form => (
					<Button
						key={_form}
						m="16px"
						color={_form === form ? theme.colors.white : null}
						bg={_form === form ? theme.colors.blue[700] : null}
						_hover={{
							backgroundColor: _form === form ? theme.colors.blue[800] : theme.colors.blue[700],
							color: _form === form ? null : theme.colors.white
						}}
						onClick={() => {
							const sameForm = _form === form;

							setForm(sameForm ? '' : _form);

							if (sameForm) {
								clearSearch(false);
							} else {
								search(_form);
							}
						}}
					>
						{forms[_form]}
					</Button>
				))}
			</Flex>
			<Flex wrap="wrap">
				<Flex>
					<Box position="relative">
						<Box
							w="140px"
							p="8px"
							m="8px"
							borderRadius="base"
							onClick={() =>
								setCalendar({
									start: !calendar.start,
									end: false
								})
							}
						>
							<Text as="label" pb="4px">
								Start Date
							</Text>
							<DatePicker
								popperPlacement="bottom-end"
								maxDate={endDate || null}
								selected={startDate}
								onChange={setStartDate}
								showMonthDropdown
								showYearDropdown
								dropdownMode="scroll"
								customInput={
									<Input bgColor={theme.colors.white} borderRadius="base" textAlign="center" />
								}
							/>
						</Box>
					</Box>
					<Box position="relative">
						<Box
							w="140px"
							p="8px"
							m="8px"
							borderRadius="base"
							onClick={() =>
								setCalendar({
									start: false,
									end: !calendar.end
								})
							}
						>
							<Text as="label" pb="4px">
								End Date
							</Text>
							<DatePicker
								minDate={startDate || null}
								selected={endDate}
								onChange={setEndDate}
								showMonthDropdown
								showYearDropdown
								dropdownMode="scroll"
								customInput={
									<Input bgColor={theme.colors.white} borderRadius="base" textAlign="center" />
								}
							/>
						</Box>
					</Box>
					<Box p="8px" m="8px" borderRadius="base" w="246px" minW="246px">
						<Text as="label" pb="4px">
							Location
						</Text>
						<Autocomplete
							bgColor={theme.colors.white}
							borderRadius="base"
							dataList={locationList}
							fieldName="project"
							value={location}
							onChange={value => setLocation(value)}
							onSelected={value => setLocation(value)}
						/>
					</Box>
				</Flex>
				<Flex>
					{!form ? (
						<Box p="8px" m="8px" borderRadius="base">
							<Text as="label" pb="4px">
								Return Form
							</Text>
							<NativeSelect.Root size="sm">
								<NativeSelect.Field
									w="230px"
									minW="230px"
									bgColor={theme.colors.white}
									placeholder="- Select a return form -"
									invalid={!returnForm}
									onChange={({ target }) => {
										setReturnForm(target.value);

										if (target.value === 'safety_inspection') {
											setHasPictures(false);
										}
									}}
									value={returnForm}
									borderRadius="base"
								>
									{Object.entries(forms).map(([key, title], index) => (
										<option key={`${key}_${index}`} value={key}>
											{title}
										</option>
									))}
								</NativeSelect.Field>
								<NativeSelect.Indicator />
							</NativeSelect.Root>
						</Box>
					) : null}
					<Box p="8px" m="8px" borderRadius="base" w="246px" minW="246px">
						<Text as="label" pb="4px">
							Completed By
						</Text>
						<Autocomplete
							bgColor={theme.colors.white}
							borderRadius="base"
							dataList={employeeList}
							fieldName="employee"
							value={observer}
							onChange={value => setObserver(value)}
							onSelected={value => setObserver(value)}
						/>
					</Box>
					<Box p="8px" m="8px" borderRadius="base" w="246px" minW="246px">
						<Text as="label" pb="4px">
							Supervisor
						</Text>
						<Autocomplete
							bgColor={theme.colors.white}
							borderRadius="base"
							dataList={employeeList}
							fieldName="employee"
							value={supervisor}
							onChange={value => setSupervisor(value)}
							onSelected={value => setSupervisor(value)}
						/>
					</Box>
					<Box p="8px" m="8px" borderRadius="base" w="110px" minW="110px">
						<Text as="label" pb="4px">
							Has Pictures
						</Text>
						<Switch.Root
							disabled={
								(!returnForm && form === 'safety_inspection') || returnForm === 'safety_inspection'
							}
							size="lg"
							checked={hasPictures}
							onCheckedChange={() => setHasPictures(!hasPictures)}
						>
							<Switch.HiddenInput />
							<Switch.Control />
						</Switch.Root>
					</Box>
				</Flex>
			</Flex>
			{filters.map((filter, index) => (
				<VStack key={index} alignItems="flex-start">
					{index ? (
						<RadioGroup.Root
							mx="8px"
							px="8px"
							value={operator}
							onValueChange={({ value }) => setOperator(value)}
						>
							<RadioGroup.Item key="and" value="and">
								<RadioGroup.ItemHiddenInput />
								<RadioGroup.ItemIndicator />
								<RadioGroup.ItemText>And</RadioGroup.ItemText>
							</RadioGroup.Item>
							<RadioGroup.Item key="or" value="or">
								<RadioGroup.ItemHiddenInput />
								<RadioGroup.ItemIndicator />
								<RadioGroup.ItemText>Or</RadioGroup.ItemText>
							</RadioGroup.Item>
						</RadioGroup.Root>
					) : null}
					<HStack>
						<Box p="8px" m="8px">
							<Text as="label" pb="4px">
								Filter Category
							</Text>
							<NativeSelect.Root size="sm">
								<NativeSelect.Field
									disabled={
										(!returnForm && form === 'safety_inspection') ||
										returnForm === 'safety_inspection'
									}
									bgColor={theme.colors.white}
									placeholder="- Select a filter category -"
									onChange={({ target }) =>
										setFilters(prevFilters =>
											prevFilters.map((prevFilter, _index) =>
												_index === index
													? {
															category: target.value,
															type: '',
															select: ''
														}
													: prevFilter
											)
										)
									}
									value={filters[index].category}
								>
									{Object.entries(filterCategories).map(([key, title], _index) => (
										<option key={`${key}_${_index}`} value={key}>
											{title}
										</option>
									))}
								</NativeSelect.Field>
								<NativeSelect.Indicator />
							</NativeSelect.Root>
						</Box>
						<Box p="8px" m="8px">
							<Text as="label" pb="4px">
								Filter Type
							</Text>
							<NativeSelect.Root size="sm">
								<NativeSelect.Field
									bgColor={theme.colors.white}
									disabled={
										(!returnForm && form === 'safety_inspection') ||
										returnForm === 'safety_inspection' ||
										!filters[index].category
									}
									placeholder="- Select -"
									onChange={({ target }) =>
										setFilters(prevFilters =>
											prevFilters.map((prevFilter, _index) =>
												_index === index
													? {
															...prevFilter,
															type: target.value,
															select: ''
														}
													: prevFilter
											)
										)
									}
									value={filters[index].type}
								>
									{Object.entries(selectOptions[filters[index].category] || {})
										.filter(([key]) => key !== 'select')
										.map(([key, title], _index) => (
											<option key={`${key}_${_index}`} value={key}>
												{title}
											</option>
										))}
								</NativeSelect.Field>
								<NativeSelect.Indicator />
							</NativeSelect.Root>
						</Box>
						<Box p="8px" m="8px">
							<Text as="label" pb="4px">
								Select
							</Text>
							<NativeSelect.Root size="sm">
								<NativeSelect.Field
									bgColor={theme.colors.white}
									disabled={
										(!returnForm && form === 'safety_inspection') ||
										returnForm === 'safety_inspection' ||
										!filters[index].category ||
										!Object.prototype.hasOwnProperty.call(
											selectOptions[filters[index].category] || {},
											'select'
										)
									}
									placeholder="- Select -"
									onChange={({ target }) =>
										setFilters(prevFilters =>
											prevFilters.map((prevFilter, _index) =>
												_index === index
													? {
															...prevFilter,
															select: target.value
														}
													: prevFilter
											)
										)
									}
									value={filters[index].select}
									borderRadius="base"
								>
									{Object.entries(selectOptions[filters[index].category]?.select || {}).map(
										([key, title], _index) => (
											<option key={`${key}_${_index}`} value={key}>
												{title}
											</option>
										)
									)}
								</NativeSelect.Field>
								<NativeSelect.Indicator />
							</NativeSelect.Root>
						</Box>
						<Box p="8px" m="8px" borderRadius="base" alignSelf="flex-end">
							<CloseButton
								size="md"
								m="8px"
								color={theme.colors.white}
								bgColor={theme.colors.red[500]}
								_hover={{ background: theme.colors.red[600] }}
								onClick={() => setRemoveFilterDialogOpen(index)}
							/>
							<Dialog.Root
								role="alertdialog"
								initialFocusEl={filterRefs.map.get(index)}
								onOpenChange={({ open }) => {
									if (!open) {
										setRemoveFilterDialogOpen(null);
									}
								}}
								open={removeFilterDialogOpen === index}
								placement="center"
							>
								<Portal>
									<Dialog.Backdrop />
									<Dialog.Positioner>
										<Dialog.Content opacity="1 !important">
											<Dialog.Header>
												<Dialog.Title fontSize="lg" fontWeight="bold">
													Confirm Delete
												</Dialog.Title>
											</Dialog.Header>
											<Dialog.Body>
												<p>Are you sure you want to remove this item?</p>
											</Dialog.Body>
											<Dialog.Footer>
												<Dialog.ActionTrigger asChild>
													<Button
														size="lg"
														ref={filterRefs.ref(index)}
														onClick={() => setRemoveFilterDialogOpen(null)}
													>
														Cancel
													</Button>
												</Dialog.ActionTrigger>
												<Button
													colorPalette="red"
													size="lg"
													ml={3}
													bg={theme.colors.red[500]}
													_hover={{ background: theme.colors.red[600] }}
													color={theme.colors.white}
													onClick={() => {
														setRemoveFilterDialogOpen(setRemoveFilterDialogOpen(null));
														setFilters(prevFilters => {
															delete prevFilters[index];

															return prevFilters.filter(Boolean);
														});
													}}
												>
													Delete Item
												</Button>
											</Dialog.Footer>
											<Dialog.CloseTrigger asChild>
												<CloseButton size="sm" />
											</Dialog.CloseTrigger>
										</Dialog.Content>
									</Dialog.Positioner>
								</Portal>
							</Dialog.Root>
						</Box>
					</HStack>
				</VStack>
			))}
			<Flex>
				<Button
					m="16px"
					color={theme.colors.white}
					bg={theme.colors.blue[700]}
					_hover={{ backgroundColor: theme.colors.blue[800] }}
					disabled={(!returnForm && form === 'safety_inspection') || returnForm === 'safety_inspection'}
					onClick={() => setFilters(prevFilters => [...prevFilters, { category: '', type: '', select: '' }])}
				>
					Add Filter
				</Button>
			</Flex>
			<Flex>
				<Button
					m="16px"
					color={theme.colors.white}
					bg={theme.colors.blue[700]}
					_hover={{ backgroundColor: theme.colors.blue[800] }}
					disabled={!(startDate && endDate && (form || returnForm))}
					onClick={() => search()}
				>
					Search
				</Button>
				<Button
					m="16px"
					color={theme.colors.white}
					bg="#737373"
					_hover={{ backgroundColor: theme.colors.gray[700] }}
					onClick={clearSearch}
				>
					Clear
				</Button>
			</Flex>
			{hasSearched && (
				<>
					<Separator mb="16px" />
					<Text m="16px">
						{Object.keys(rowSelection).length} / {table.getPreFilteredRowModel().rows.length} Generated at{' '}
						{searchTime}
					</Text>
					<Table.Root className="safety-database-results">
						<Table.Header>
							{table.getHeaderGroups().map((headerGroup, headerIndex) => (
								<Table.Row id={headerIndex} key={headerIndex}>
									{headerGroup.headers.map((header, columnIndex) => (
										<Table.ColumnHeader
											key={columnIndex}
											id={header.id}
											colSpan={header.colSpan}
											onClick={header.column.getToggleSortingHandler()}
										>
											{flexRender(header.column.columnDef.header, header.getContext())}
											{{
												asc: (
													<chakra.span pl="4">
														<Icon aria-label="sorted ascending">
															<FaSortUp />
														</Icon>
													</chakra.span>
												),
												desc: (
													<chakra.span pl="4">
														<Icon aria-label="sorted descending">
															<FaSortDown />
														</Icon>
													</chakra.span>
												)
											}[header.column.getIsSorted()] ?? null}
										</Table.ColumnHeader>
									))}
								</Table.Row>
							))}
						</Table.Header>
						<Table.Body>
							{table.getRowModel().rows.map(row => (
								<Table.Row key={row.index}>
									{row.getAllCells().map((cell, cellIndex) => (
										<Table.Cell
											key={cell.index}
											isNumeric={cell.column.isNumeric}
											onClick={() => {
												// Ignore checkbox select
												// row cell
												if (cellIndex > 0) {
													window.open(
														window.location.origin +
															'/dashboard/safety-database/' +
															row.original.id +
															'/' +
															row.original.sid,
														'_blank'
													);
												}
											}}
										>
											{flexRender(cell.column.columnDef.cell, cell.getContext())}
										</Table.Cell>
									))}
								</Table.Row>
							))}
						</Table.Body>
					</Table.Root>
					{!data.length && <Text>No Results Found</Text>}
				</>
			)}
			{table.getSelectedRowModel().flatRows.length ? (
				<>
					<Button
						m="16px"
						onClick={() =>
							handlePDFExport(table.getSelectedRowModel().flatRows.map(row => row.original.sid))
						}
					>
						Print Result Set as PDF
					</Button>
					<Button
						m="16px"
						onClick={() =>
							handleExcelExport(
								mapValues(
									groupBy(
										table
											.getSelectedRowModel()
											.flatRows.map(({ original: { sid, id } }) => ({ sid, id })),
										'id'
									),
									rows => rows.map(({ sid }) => Number(sid))
								)
							)
						}
					>
						Print Result Set as Excel
					</Button>
				</>
			) : null}
		</Box>
	);
};
